import { traduzirErroSupabase } from '@octawash/shared';
import { useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, Eye, ShieldCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

export function LgpdPage() {
  const { tenant } = useTenant();
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);

  const exportar = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('exportar-dados-titular', {
        body: {},
      });
      if (error) throw error;
      return data as { url_assinada: string; qtd_tabelas: number };
    },
    onSuccess: (r) => {
      toast.success(`Pacote pronto! ${r.qtd_tabelas} tabelas`);
      window.open(r.url_assinada, '_blank');
    },
    onError: (e: Error) => toast.error('Falha exportar', { description: traduzirErroSupabase(e) }),
  });

  const solicitarExclusao = useMutation({
    mutationFn: async () => {
      const { data: cli } = await supabase
        .from('tab_clientes')
        .select('seq_cliente, seq_tenant')
        .single();
      if (!cli) throw new Error('Cliente não encontrado');
      const { error } = await supabase.from('tab_solicitacoes_titular').insert({
        seq_tenant: cli.seq_tenant,
        seq_cliente: cli.seq_cliente,
        cod_tipo_solicitacao: 'EXCLUSAO',
        des_solicitacao:
          'Solicitacao de exclusao/anonimização via web-client. DPO válida em até 15 dias.',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Solicitacao registrada', {
        description: 'O DPO vai entrar em contato em até 15 dias',
      });
      setConfirmandoExclusao(false);
    },
    onError: (e: Error) => toast.error('Falha', { description: traduzirErroSupabase(e) }),
  });

  return (
    <div className="space-y-5 p-4 pb-24">
      <motion.div
        className="octa-page-header"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="bg-octa-gradient shadow-octa-glow mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <h1>Meus dados (LGPD)</h1>
        <p>
          Direitos garantidos pela LGPD (Lei 13.709/2018). Exporte, corrija ou exclua a qualquer
          momento.
        </p>
      </motion.div>

      {/* Exportar */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08, duration: 0.45 }}
      >
        <Card variant="default">
          <CardContent className="space-y-3 p-5">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-xl">
                <Download className="h-5 w-5" />
              </div>
              <h2 className="text-base font-bold tracking-tight">Exportar meus dados</h2>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Receba um JSON com todos os seus dados: cadastro, veículos, lavagens, pagamentos,
              consentimentos e logs.
            </p>
            <Button
              size="xl"
              onClick={() => exportar.mutate()}
              disabled={exportar.isPending}
              className="bg-octa-gradient shadow-octa-glow w-full text-white"
            >
              <Download className="mr-2 h-4 w-4" />
              {exportar.isPending ? 'Gerando pacote...' : 'Exportar agora'}
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      {/* Documentos */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.16, duration: 0.45 }}
      >
        <Card variant="default">
          <CardContent className="space-y-3 p-5">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-xl">
                <Eye className="h-5 w-5" />
              </div>
              <h2 className="text-base font-bold tracking-tight">Documentos</h2>
            </div>
            <div className="space-y-1">
              {tenant?.des_url_termos && (
                <a
                  href={tenant.des_url_termos}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:bg-accent flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  Ler Termos de Uso
                </a>
              )}
              {tenant?.des_url_pol_priv && (
                <a
                  href={tenant.des_url_pol_priv}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:bg-accent flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  Ler Politica de Privacidade
                </a>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Excluir */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.24, duration: 0.45 }}
      >
        <Card variant="default" className="border-rose-200/70 dark:border-rose-900/40">
          <CardContent className="space-y-3 p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400">
                <Trash2 className="h-5 w-5" />
              </div>
              <h2 className="text-base font-bold tracking-tight text-rose-700 dark:text-rose-300">
                Excluir minha conta
              </h2>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Anonimiza seus dados pessoais.{' '}
              <strong className="text-foreground">Histórico financeiro fica preservado</strong> por
              5 anos por exigência fiscal. Após confirmação do DPO (até 15 dias), seu acesso será
              revogado.
            </p>
            <AnimatePresence mode="wait">
              {confirmandoExclusao ? (
                <motion.div
                  key="confirm"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="space-y-3 rounded-xl bg-rose-50 p-3 dark:bg-rose-950/40"
                >
                  <p className="text-sm font-bold text-rose-700 dark:text-rose-300">
                    Tem certeza? Acao irreversivel.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="lg"
                      onClick={() => solicitarExclusao.mutate()}
                      disabled={solicitarExclusao.isPending}
                      className="flex-1"
                    >
                      Sim, solicitar exclusao
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => setConfirmandoExclusao(false)}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="trigger"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Button
                    variant="outline"
                    size="xl"
                    onClick={() => setConfirmandoExclusao(true)}
                    className="w-full border-rose-300/60 text-rose-600 hover:bg-rose-50 dark:border-rose-900/60 dark:text-rose-400 dark:hover:bg-rose-950/40"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Solicitar exclusao
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </motion.div>

      {tenant?.des_email_dpo && (
        <p className="text-muted-foreground pt-2 text-center text-xs">
          Dúvidas? DPO:{' '}
          <a
            href={`mailto:${tenant.des_email_dpo}`}
            className="text-primary font-semibold hover:underline"
          >
            {tenant.des_email_dpo}
          </a>
        </p>
      )}
    </div>
  );
}
